import { Leaf, TreePine } from 'lucide-react';
import type { AgBlockEntry } from '../types';

const RISK_STYLE_MODERATE = 'bg-amber-500/20 text-amber-100 border-amber-400/40';

export default function BlockCard({
  block,
  sourceNote,
  adminHref,
}: {
  block: AgBlockEntry;
  sourceNote: string;
  // Link to this farm's "Farms & blocks" editor in /admin.
  adminHref: string;
}) {
  const tone = !block.status
    ? 'bg-white/10 border-white/10'
    : block.status === 'very-dry'
      ? 'bg-amber-600/25 border-amber-400/60'
      : block.status === 'dry'
        ? 'bg-amber-400/20 border-amber-300/40'
        : block.status === 'wet'
          ? 'bg-emerald-500/20 border-emerald-400/40'
          : 'bg-emerald-600/15 border-emerald-500/30';

  const verdict = !block.status
    ? 'Measured ET data unavailable — showing forecast only'
    : block.status === 'very-dry'
      ? 'Significant deficit — irrigate soon'
      : block.status === 'dry'
        ? 'Deficit — schedule irrigation'
        : block.status === 'wet'
          ? 'Rain exceeded crop water use — skip irrigation'
          : 'Water balance adequate';

  const Icon = block.crop === 'olive' ? TreePine : Leaf;
  const heroDim = block.configured ? '' : 'opacity-50';

  return (
    <div className={`rounded-lg p-3 border backdrop-blur-sm ${tone}`}>
      <div className="flex items-start gap-2">
        <Icon className="w-4 h-4 shrink-0 mt-0.5 text-emerald-200" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider opacity-70">
            {block.cropLabel}
            {block.variety ? ` · ${block.variety}` : ''} (Kc {block.kc.effective.toFixed(2)})
          </div>
          <div className="text-sm font-medium">{block.name}</div>
          <div className="text-sm font-medium">{verdict}</div>
          <div className="text-xs opacity-85 tabular-nums mt-1">
            ETc today {block.etcTodayIn.toFixed(2)}"
            <span className="opacity-60"> · next 7d {block.etcNext7In.toFixed(2)}"</span>
            {block.effRainNext7In > 0 && (
              <span className="opacity-60"> · rain credit {block.effRainNext7In.toFixed(2)}"</span>
            )}
          </div>
        </div>
        {!block.configured && (
          <a
            href={adminHref}
            className={`text-[10px] px-2 py-1 rounded border shrink-0 hover:underline ${RISK_STYLE_MODERATE}`}
          >
            Placeholder — set up this block in /admin
          </a>
        )}
      </div>

      <div className={`mt-3 pt-3 border-t border-white/10 ${heroDim}`}>
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider opacity-70">Run drip this week</div>
            <div className="text-2xl font-light tabular-nums">
              {block.forecast.hoursPerWeek.toFixed(1)}
              <span className="text-sm opacity-70 ml-1">hrs</span>
            </div>
            <div className="text-[10px] opacity-60">
              ~{block.forecast.gallonsPerPlant} gal / plant · next 7 days, net of forecast rain
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-wider opacity-70">Block total</div>
            <div className="text-2xl font-light tabular-nums">
              {block.forecast.gallonsTotal.toLocaleString()}
              <span className="text-sm opacity-70 ml-1">gal</span>
            </div>
          </div>
        </div>

        {block.measured && (
          <div className="text-[10px] opacity-70 mt-2">
            Last 7 days (measured): deficit {block.measured.deficitIn.toFixed(2)}" →{' '}
            {block.measured.hoursPerWeek.toFixed(1)} hrs · {block.measured.gallonsTotal.toLocaleString()} gal
          </div>
        )}

        <div className="text-[10px] opacity-60 mt-2 tabular-nums">
          {block.layout.plantCount} plants · {block.layout.acreage} ac ({block.layout.rowSpacingFt}×
          {block.layout.inRowSpacingFt} ft rows) · {block.layout.emittersPerPlant} ×{' '}
          {block.layout.gphPerEmitter} GPH per plant · drip efficiency {block.layout.efficiencyPct}% · canopy{' '}
          {block.kc.canopyCoverPct}%/{block.kc.matureCoverPct}% → maturity {block.kc.maturityFactor}
        </div>
      </div>

      <div className="text-[10px] opacity-50 mt-2">
        Kc {block.kc.base.toFixed(2)} — {block.kc.source}
        {block.kc.estimate && (
          <span className={`ml-2 px-1.5 py-0.5 rounded border ${RISK_STYLE_MODERATE}`}>estimate — calibrate</span>
        )}
        <div>ETc = ET₀ × Kc × canopy factor. Runtime = (ETc − effective rain) ÷ drip efficiency.</div>
        <div className="mt-1 flex gap-3">
          <a href={adminHref} className="hover:underline underline-offset-2">
            Edit block
          </a>
          <a href="/admin?tab=kc" className="hover:underline underline-offset-2">
            Edit Kc table
          </a>
        </div>
      </div>

      <div className="text-[10px] opacity-50 mt-1">{sourceNote}</div>
    </div>
  );
}
